import React, { useState, useEffect } from "react";
import { baseURL } from "../../App";
import "./admin.css";
import axios from "axios";
import Cookies from "js-cookie";
import { FaTrash, FaEdit, FaSave } from "react-icons/fa";

const ServiceAdmin = () => {
  const API_URL = `${baseURL}/api/services`; // Corrected API endpoint
  const [serviceData, setServiceData] = useState([]);
  const [newService, setNewService] = useState({
    titleEn: "",
    titleFr: "",
    items: [],
    image: null,
  });
  const [newItem, setNewItem] = useState({
    descriptionEn: "",
    descriptionFr: "",
  });
  const [editingServiceIndex, setEditingServiceIndex] = useState(-1);
  const [editingItemIndex, setEditingItemIndex] = useState(-1);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const token = Cookies.get("token");

  useEffect(() => {
    const fetchServiceData = async () => {
      try {
        const response = await axios.get(API_URL, { withCredentials: true });
        setServiceData(response.data);
      } catch (err) {
        console.error("Error fetching Service data:", err);
        setConfirmationMessage("Error fetching data. Please try again later.");
      }
    };

    fetchServiceData();
  }, [API_URL]);

  const handleAddService = async () => {
    if (newService.titleEn.trim() === "" || newService.titleFr.trim() === "") {
      setConfirmationMessage("Please fill in both title fields.");
      return; // Don't proceed if titles are empty
    }

    const data = new FormData();
    if (newService.image instanceof File) {
      data.append("file", newService.image);
    }
    data.append("titleEn", newService.titleEn);
    data.append("titleFr", newService.titleFr);
    data.append("items", JSON.stringify(newService.items));

    try {
      const response = await axios.post(API_URL, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });
      setServiceData([...serviceData, response.data]);
      setNewService({ titleEn: "", titleFr: "", items: [], image: null });
      setConfirmationMessage("Service added successfully!");
    } catch (error) {
      const msg = error.response?.data?.message;
      setConfirmationMessage(
        Array.isArray(msg) ? msg.join(", ") : msg || "Failed to add service.",
      );
      console.error("Add Service Error:", error.response?.data);
    }
  };

  const handleAddItem = (serviceIndex) => {
    if (
      newItem.descriptionEn.trim() !== "" &&
      newItem.descriptionFr.trim() !== ""
    ) {
      const updatedServiceData = [...serviceData];
      updatedServiceData[serviceIndex].items.push({
        ...newItem,
        id: Date.now(),
      });
      setServiceData(updatedServiceData); // Temporary frontend update
      setNewItem({ descriptionEn: "", descriptionFr: "" });

      // Call updateService to persist the changes to the backend:
      updateService(serviceIndex); // Save changes to database
    } else {
      setConfirmationMessage("Please enter descriptions for both languages.");
    }
  };

  const handleEditService = (serviceIndex) => {
    setNewService({ ...serviceData[serviceIndex] });
    setEditingServiceIndex(serviceIndex);
  };

  const handleSaveService = async (serviceIndex) => {
    const data = new FormData();
    if (newService.image instanceof File) {
      data.append("file", newService.image);
    }
    data.append("titleEn", newService.titleEn);
    data.append("titleFr", newService.titleFr);
    data.append("items", JSON.stringify(newService.items));

    try {
      // Use PATCH for service updates
      const response = await axios.patch(
        `${API_URL}/${serviceData[serviceIndex]._id}`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        },
      );
      const updatedServiceData = [...serviceData];
      updatedServiceData[serviceIndex] = response.data;
      setServiceData(updatedServiceData);
      setEditingServiceIndex(-1);
      setNewService({ titleEn: "", titleFr: "", items: [] }); // Clear the form
      setConfirmationMessage("Service Updated successfully");
    } catch (error) {
      const msg = error.response?.data?.message;
      setConfirmationMessage(
        Array.isArray(msg)
          ? msg.join(", ")
          : msg || "Failed to update service.",
      );
      console.error("Update Service Error:", error.response?.data);
    }
  };

  const handleDeleteService = async (serviceIndex) => {
    try {
      await axios.delete(`${API_URL}/${serviceData[serviceIndex]._id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });

      const updatedServices = serviceData.filter((_, i) => i !== serviceIndex);
      setServiceData(updatedServices);
      setConfirmationMessage("Service deleted successfully!");
    } catch (error) {
      console.error("Error deleting service:", error);
      setConfirmationMessage(
        error?.response?.data?.message || "Failed to delete service.",
      );
    }
  };

  const handleEditItem = (serviceIndex, itemIndex) => {
    setNewItem(serviceData[serviceIndex].items[itemIndex]);
    setEditingItemIndex(itemIndex);
    setEditingServiceIndex(serviceIndex);
  };

  const handleSaveItem = (serviceIndex, itemIndex) => {
    const updatedServiceData = [...serviceData];
    updatedServiceData[serviceIndex].items[itemIndex] = newItem;
    setServiceData(updatedServiceData); // Temporary frontend update
    setNewItem({ descriptionEn: "", descriptionFr: "" });
    setEditingItemIndex(-1);
    setEditingServiceIndex(-1);

    updateService(serviceIndex); //  Save the change to the database
  };

  const handleRemoveItem = (serviceIndex, itemIndex) => {
    const updatedServiceData = [...serviceData];
    updatedServiceData[serviceIndex].items.splice(itemIndex, 1);
    setServiceData(updatedServiceData);
  };

  const updateService = async (serviceIndex) => {
    try {
      const serviceToUpdate = serviceData[serviceIndex];
      const response = await axios.put(
        `${API_URL}/${serviceToUpdate._id}`,
        serviceToUpdate,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        },
      );

      if (response.status === 200) {
        // Check for success status
        const updatedServiceData = [...serviceData];
        updatedServiceData[serviceIndex] = response.data; // Update the serviceData with the response from the server
        setServiceData(updatedServiceData);
        setConfirmationMessage("Item added successfully!");
      } else {
        setConfirmationMessage("Failed to add item - Server error."); // More specific server error message
      }
    } catch (error) {
      console.error("Error updating service:", error);
      setConfirmationMessage(
        error?.response?.data?.message || "Failed to add item.",
      );
    }
  };

  return (
    <div className="admin-section-service">
      <h2>Service Management</h2>
      <div className="admin-form">
        {/* Add New Service Section */}
        <h3>Add New Service</h3>
        <div className="form-group">
          <label>Service Title (EN)</label>
          <input
            type="text"
            placeholder="Title (EN)"
            value={newService.titleEn}
            onChange={(e) =>
              setNewService({ ...newService, titleEn: e.target.value })
            }
          />
        </div>
        <div className="form-group">
          <label>Service Title (FR)</label>
          <input
            type="text"
            placeholder="Title (FR)"
            value={newService.titleFr}
            onChange={(e) =>
              setNewService({ ...newService, titleFr: e.target.value })
            }
          />
        </div>
        <div className="form-group">
          <label>Service Icon/Image</label>
          <input
            type="file"
            onChange={(e) =>
              setNewService({ ...newService, image: e.target.files[0] })
            }
          />
        </div>
        <button
          type="button"
          className="btn btnSecondary"
          onClick={handleAddService}
        >
          Add Service
        </button>
      </div>
      {/* Existing Services */}
      {serviceData.map((service, serviceIndex) => (
        <div key={service._id} className="service-item">
          {editingServiceIndex === serviceIndex ? (
            <div className="admin-form">
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Title (EN)"
                  value={newService.titleEn}
                  onChange={(e) =>
                    setNewService({ ...newService, titleEn: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Title (FR)"
                  value={newService.titleFr}
                  onChange={(e) =>
                    setNewService({ ...newService, titleFr: e.target.value })
                  }
                />
              </div>
              <div className="icon-actions">
                <FaSave
                  className="save-icon"
                  onClick={() => handleSaveService(serviceIndex)}
                />
              </div>
            </div>
          ) : (
            <h3>
              {service.titleEn} (EN) / {service.titleFr} (FR)
              <div>
                <FaEdit
                  className="edit-icon"
                  onClick={() => handleEditService(serviceIndex)}
                />
                <FaTrash
                  className="delete-icon"
                  onClick={() => handleDeleteService(serviceIndex)}
                />
              </div>
            </h3>
          )}

          {/* Add/Edit Item Section */}
          <input
            type="text"
            placeholder="New Item Description (EN)"
            value={newItem.descriptionEn}
            onChange={(e) =>
              setNewItem({ ...newItem, descriptionEn: e.target.value })
            }
          />
          <input
            type="text"
            placeholder="New Item Description (FR)"
            value={newItem.descriptionFr}
            onChange={(e) =>
              setNewItem({ ...newItem, descriptionFr: e.target.value })
            }
          />
          <button
            type="button"
            className="btn btnSecondary"
            onClick={() => handleAddItem(serviceIndex)}
          >
            Add Item
          </button>

          {/* Display/Edit existing items */}
          <ul>
            {service.items.map((item, itemIndex) => (
              <li key={item.id}>
                {editingItemIndex === itemIndex &&
                editingServiceIndex === serviceIndex ? (
                  <>
                    <input
                      type="text"
                      value={newItem.descriptionEn}
                      onChange={(e) =>
                        setNewItem({
                          ...newItem,
                          descriptionEn: e.target.value,
                        })
                      }
                    />
                    <input
                      type="text"
                      value={newItem.descriptionFr}
                      onChange={(e) =>
                        setNewItem({
                          ...newItem,
                          descriptionFr: e.target.value,
                        })
                      }
                    />
                    <FaSave
                      onClick={() => handleSaveItem(serviceIndex, itemIndex)}
                    />
                  </>
                ) : (
                  <>
                    <span>
                      {item.descriptionEn} (EN) / {item.descriptionFr} (FR){" "}
                    </span>

                    <FaEdit
                      onClick={() => handleEditItem(serviceIndex, itemIndex)}
                    />
                    <FaTrash
                      onClick={() => handleRemoveItem(serviceIndex, itemIndex)}
                    />
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
      {confirmationMessage && <p>{confirmationMessage}</p>}{" "}
      {/* Display confirmation messages */}
    </div>
  );
};

export default ServiceAdmin;
